import React from 'react';
import classNames from 'classnames';
import { Box, Card, Skeleton, Typography } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import { useMaxWidthTabletLarge } from 'hooks/useMediaQueries';
import type { WaveDistributionTimestep } from 'types/cameraInsights';
import type { GraphDataError } from 'types/graphs';
import type { Units } from 'types/units';

import LiveIndicator from '../LiveIndicator';
import WaveDistributionGraph from './components/WaveDistributionGraph/WaveDistributionGraph';

import styles from './WaveDistribution.module.scss';
import camInsightsStyles from '../../CameraInsights.module.scss';

const WaveDistributionHeader: React.FC<{ isLive?: boolean }> = ({ isLive }) => {
  const isMobileView = useMaxWidthTabletLarge();

  return (
    <Box className={camInsightsStyles.header}>
      <LiveIndicator isLive={isLive} />
      <Typography
        className={camInsightsStyles.headerTitle}
        variant={isMobileView ? 'callout2' : 'subtitle1'}
      >
        Wave Distribution
      </Typography>
    </Box>
  );
};

interface WaveDistributionContentProps {
  compareMessage?: string;
  data?: WaveDistributionTimestep[];
  isLive?: boolean;
  isLoading?: boolean;
  isResizing?: boolean;
  units?: Pick<Units, 'waves'>;
}

const WaveDistributionContent = ({
  compareMessage,
  data,
  isLive,
  isLoading,
  isResizing,
  units,
}: WaveDistributionContentProps) => (
  <Box className={classNames(camInsightsStyles.insightsModule, styles.waveDistribution)}>
    <Card
      className={camInsightsStyles.insightsModuleCard}
      data-testid="wave-distribution"
      elevation={0}
    >
      <WaveDistributionHeader isLive={isLive} />
      {isLoading || isResizing ? (
        <Box data-testid="wave-distribution-loading">
          <Skeleton
            animation="pulse"
            className={camInsightsStyles.loading}
            sx={{ height: '91px', margin: '8px 0' }}
            variant="rounded"
          />
          <Skeleton
            animation="pulse"
            className={camInsightsStyles.loading}
            sx={{ height: '20px', width: '60%' }}
            variant="text"
          />
        </Box>
      ) : (
        <>
          <WaveDistributionGraph data={data} units={units} />
          <Typography
            className={styles.compareMessage}
            component="p"
            variant="caption1"
            data-testid="wave-distribution-message"
          >
            {compareMessage}
          </Typography>
        </>
      )}
    </Card>
  </Box>
);

interface WaveDistributionProps extends WaveDistributionContentProps {
  error?: GraphDataError | boolean;
}

const WaveDistribution = ({
  compareMessage,
  data,
  error,
  isLoading,
  isLive,
  isResizing,
  units,
}: WaveDistributionProps) => (
  <ErrorBoundary
    error={!!error}
    errorBoundaryId="camera-insights-wave-distribution"
    render={() => (
      <Box className={classNames(camInsightsStyles.insightsModule, styles.waveDistribution)}>
        <Card
          className={camInsightsStyles.error}
          data-testid="wave-distribution-error"
          elevation={0}
        >
          <WaveDistributionHeader isLive={isLive} />
          <ErrorMessage
            Icon={error ? null : undefined}
            className={camInsightsStyles.errorMessage}
            message={(error as GraphDataError)?.message}
          />
        </Card>
      </Box>
    )}
  >
    <WaveDistributionContent
      compareMessage={compareMessage}
      data={data}
      isLive={isLive}
      isLoading={isLoading}
      isResizing={isResizing}
      units={units}
    />
  </ErrorBoundary>
);
export default WaveDistribution;
